import { ButtonGhost, Icon } from '@amelio/ui-library';

import { GROWTH_GLASS_SURFACE, GROWTH_SECTION_PADDING, GROWTH_SECTION_TITLE } from './growthStyles';
import { PerformanceScoreDial } from '../PerformanceScoreDial';

import type { PerformanceScore } from '@/types/performance-profile';

import { useAmelioTranslations } from '@/hooks';

interface GrowthPerformanceScoreProps {
    score: PerformanceScore;
    currentProfileName: string | null;
    /** Opens the page's competency-profile assignment flow. */
    onEditCurrentProfile: () => void;
    /** The period the score covers; the filter that set it lives on the Évaluation tab. */
    scorePeriodLabel: string | null;
    dataTestId?: string;
}

/**
 * The performance score of the "Croissance" tab: the same dial as the overview, next to the
 * next-target card, with the competency profile currently assigned underneath.
 */
export const GrowthPerformanceScore = ({
    score,
    currentProfileName,
    onEditCurrentProfile,
    scorePeriodLabel,
    dataTestId = 'growth-performance-score',
}: GrowthPerformanceScoreProps) => {
    const { t } = useAmelioTranslations();

    return (
        <section
            className={`${GROWTH_GLASS_SURFACE} ${GROWTH_SECTION_PADDING} flex flex-col items-center gap-4`}
            data-testid={dataTestId}
        >
            <h3 className={`${GROWTH_SECTION_TITLE} text-center`}>
                {t('performanceProfile.growth.score.title')}
            </h3>

            <PerformanceScoreDial
                score={score}
                periodLabel={scorePeriodLabel}
                dataTestId={dataTestId}
            />

            <div className={'flex flex-wrap items-center justify-center gap-1.5'}>
                <span className={'text-growth-muted text-xs font-medium'}>
                    {t('performanceProfile.growth.score.currentProfile')}
                </span>
                {currentProfileName ? (
                    <span className={'text-navy text-xs font-semibold'}>{currentProfileName}</span>
                ) : (
                    // Same slot, same geometry: only the words change between a profile and none.
                    // Muted and italic say the value is missing; a different size would say the
                    // tile itself changed.
                    <span className={'text-growth-slate text-xs font-semibold italic'}>
                        {t('performanceProfile.growth.nextTarget.undefined')}
                    </span>
                )}
                <ButtonGhost
                    size={'sm'}
                    onClick={onEditCurrentProfile}
                    ariaLabel={t('performanceProfile.growth.score.editCurrentProfile')}
                    dataTestId={`${dataTestId}-edit-profile`}
                    leftIcon={
                        <Icon
                            name={'edit-edit-2'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                />
            </div>
        </section>
    );
};
